import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useMemo,
} from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";
import { Doughnut } from "react-chartjs-2";
import "../../../components/Chart/ChartConfig";
import { getCalendar, createDayOff, getUserDayoffs } from "../../../api/api";
import "./Extrahours.css";
import OvertimeTable from "./OvertimeTable/OvertimeTable";
import DayoffTable from "./DayoffTable/DayoffTable";
import { UserContext } from "../../../contexts/CurrentUserContext";
import {
  getUserOvertimes,
  getUserOvertimeStats,
} from "../../../api/reportsApi";
import { formatTimeWithRounding } from "../../../utils/timeUtils";

const Extrahours = () => {
  const { userData } = useContext(UserContext);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedHours, setSelectedHours] = useState(null);
  const [calendarData, setCalendarData] = useState({});
  const [overtimeData, setOvertimeData] = useState([]);
  const [overtimePage, setOvertimePage] = useState(1);
  const [overtimePageSize] = useState(20);
  const [overtimeTotalPages, setOvertimeTotalPages] = useState(1);
  const [dayoffData, setDayoffData] = useState([]);
  const [error, setError] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [pendingHours, setPendingHours] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [overtimeStats, setOvertimeStats] = useState({
    "Не использовано": 0,
    Использовано: 0,
  });

  // Функция для подсчета часов в ожидающих заявках
  const calculatePendingHours = useCallback((dayoffs) => {
    return dayoffs.reduce((total, dayoff) => {
      // Проверяем, нет ли отказов среди ревьюеров
      const hasRejection = dayoff.reviewers.some(
        (reviewer) => reviewer.status === "Не согласован"
      );

      // Если есть хотя бы один отказ, не учитываем эти часы
      if (hasRejection) {
        return total;
      }

      // Проверяем, есть ли ревьюеры, которые еще не приняли решение
      const hasPending = dayoff.reviewers.some(
        (reviewer) => reviewer.status === "На рассмотрении"
      );

      if (hasPending) {
        const hours =
          typeof dayoff.hours === "string"
            ? parseFloat(dayoff.hours)
            : dayoff.hours;
        return total + hours;
      }
      return total;
    }, 0);
  }, []);

  // Модифицируем логику создания опций выбора часов
  const hoursOptions = useMemo(() => {
    // Преобразуем доступные часы в число
    const availableHours =
      typeof overtimeStats["Не использовано"] === "string"
        ? parseFloat(overtimeStats["Не использовано"])
        : overtimeStats["Не использовано"];

    const actualAvailableHours = Math.floor(availableHours - pendingHours);
    console.log(
      "Available hours:",
      availableHours,
      "Pending hours:",
      pendingHours,
      "Actual available:",
      actualAvailableHours
    );

    if (actualAvailableHours <= 0) return [];

    return Array.from(
      { length: Math.min(8, actualAvailableHours) },
      (_, i) => ({
        value: i + 1,
        label: `${i + 1} ${getHourLabel(i + 1)}`,
      })
    );
  }, [overtimeStats, pendingHours]);

  // Функция для правильного склонения слова "час"
  function getHourLabel(hours) {
    if (hours === 1) return "час";
    if (hours >= 2 && hours <= 4) return "часа";
    return "часов";
  }

  // Загрузка данных календаря
  const loadCalendarData = useCallback(async () => {
    try {
      const currentDate = new Date();
      const firstDay = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() - 1,
        1
      );
      const lastDay = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() + 2,
        0
      );

      const dateFrom = firstDay.toISOString().split("T")[0];
      const dateTo = lastDay.toISOString().split("T")[0];

      const data = await getCalendar(dateFrom, dateTo);
      const calendarMap = {};
      data.forEach((day) => {
        calendarMap[day.date] = day;
      });

      setCalendarData(calendarMap);
    } catch (error) {
      console.error("Ошибка при загрузке календаря:", error);
      setError("Ошибка при загрузке календаря");
    }
  }, []);

  useEffect(() => {
    loadCalendarData();
  }, [loadCalendarData]);

  // Загрузка данных о переработках
  useEffect(() => {
    setOvertimePage(1);
  }, [userData?.id]);

  useEffect(() => {
    if (overtimePage > overtimeTotalPages) {
      setOvertimePage(overtimeTotalPages);
    }
  }, [overtimePage, overtimeTotalPages]);

  useEffect(() => {
    const fetchOvertimes = async () => {
      try {
        const response = await getUserOvertimes(
          userData.id,
          overtimePage,
          overtimePageSize
        );

        const overtimes = Array.isArray(response)
          ? response
          : Array.isArray(response?.overtimes)
          ? response.overtimes
          : [];

        if (overtimes.length > 0) {
          const formattedData = overtimes.map((overtime) => {
            const reviewers = Object.entries(overtime.status || {}).map(
              ([name, status]) => ({
                name,
                status:
                  typeof status === "object" ? status.is_approved : status,
              })
            );

            return {
              date: new Date(overtime.fact_date_start).toLocaleDateString(
                "ru-RU"
              ),
              project: overtime.project_name,
              hours: formatTimeWithRounding(overtime.time_spent),
              reviewers: reviewers.filter(
                (review) => review.name && review.status
              ),
              is_used: overtime.is_used,
              is_partially_used: overtime.is_partially_used,
              used_time_spent: overtime.used_time_spent,
              used_hours:
                overtime.used_time_spent != null
                  ? formatTimeWithRounding(overtime.used_time_spent)
                  : null,
              date_of_use: overtime.date_of_use
                ? new Date(overtime.date_of_use).toLocaleDateString("ru-RU")
                : null,
              days_left: overtime.days_left,
              is_expired: Boolean(overtime.is_expired),
              expires_tomorrow: Boolean(overtime.expires_tomorrow),
            };
          });

          setOvertimeData(formattedData);
        } else {
          console.log("Получены некорректные данные:", response);
          setOvertimeData([]);
        }

        const totalCount = response?.total_count ?? response?.total;
        const totalPages =
          response?.total_pages ||
          response?.pages ||
          (totalCount && overtimePageSize
            ? Math.max(1, Math.ceil(totalCount / overtimePageSize))
            : 1);
        setOvertimeTotalPages(Math.max(1, totalPages || 1));
      } catch (error) {
        console.error("Ошибка при загрузке переработок:", error);
        setOvertimeData([]);
        setError("Ошибка при загрузке переработок");
        setOvertimeTotalPages(1);
      }
    };

    if (userData?.id) {
      fetchOvertimes();
    }
  }, [userData, overtimePage, overtimePageSize]);

  // Модифицируем loadDayoffs для корректной обработки статусов
  const loadDayoffs = useCallback(async () => {
    if (!userData?.id) return;

    try {
      const response = await getUserDayoffs(userData.id);
      const data = response.days_off;

      if (Array.isArray(data)) {
        const formattedData = data.map((dayoff) => {
          const reviewers = Object.entries(dayoff.status || {}).map(
            ([name, status]) => ({
              name,
              status:
                typeof status === "object"
                  ? status.is_approved
                    ? "Согласован"
                    : status.is_approved === false
                    ? "Не согласован"
                    : "На рассмотрении"
                  : status,
            })
          );

          const hours =
            typeof dayoff.time === "number"
              ? dayoff.time / 3600
              : parseFloat(dayoff.time);

          return {
            id: dayoff.id,
            date: new Date().toLocaleDateString("ru-RU"),
            hours: hours,
            reviewers: reviewers,
            usageDate: dayoff.date_of_use
              ? new Date(dayoff.date_of_use).toLocaleDateString("ru-RU")
              : "-",
          };
        });

        const sortedData = formattedData.sort((a, b) => b.id - a.id);
        setDayoffData(sortedData);

        // Подсчитываем и устанавливаем pending часы
        const totalPendingHours = calculatePendingHours(sortedData);
        console.log("Total pending hours calculated:", totalPendingHours);
        setPendingHours(totalPendingHours);
      }
    } catch (error) {
      console.error("Ошибка при загрузке отгулов:", error);
      setError("Ошибка при загрузке отгулов");
    }
  }, [userData, calculatePendingHours]);

  useEffect(() => {
    loadDayoffs();
  }, [loadDayoffs]);

  // Добавляем эффект для обновления pending часов при изменении dayoffData
  useEffect(() => {
    const totalPendingHours = calculatePendingHours(dayoffData);
    setPendingHours(totalPendingHours);
  }, [dayoffData, calculatePendingHours]);

  // Загрузка статистики переработок
  useEffect(() => {
    const fetchOvertimeStats = async () => {
      if (!userData?.id) return;

      try {
        const response = await getUserOvertimeStats(userData.id);
        console.log("Полученные данные о переработках:", response);

        // Конвертируем секунды в часы с десятичной дробью
        const convertToHours = (seconds) => {
          const hours = seconds / 3600;
          return Number(hours.toFixed(2)); // Округляем до 2 знаков после запятой
        };

        // Если у сотрудника нет переработок
        if (response.detail === "Нет согласованных переработок") {
          console.log("Нет согласованных переработок");
          setOvertimeStats({
            "Не использовано": 0,
            Использовано: 0,
          });
          return;
        }

        const { overtime_summary } = response;
        console.log("Данные о переработках:", {
          unused_overtime_time: overtime_summary.unused_overtime_time,
          used_overtime_time: overtime_summary.used_overtime_time,
          unused_hours: convertToHours(overtime_summary.unused_overtime_time),
          used_hours: convertToHours(overtime_summary.used_overtime_time),
        });

        setOvertimeStats({
          "Не использовано": convertToHours(
            overtime_summary.unused_overtime_time
          ),
          Использовано: convertToHours(overtime_summary.used_overtime_time),
        });
      } catch (error) {
        console.error("Ошибка при загрузке статистики переработок:", error);
        setError("Ошибка при загрузке статистики переработок");
      }
    };

    fetchOvertimeStats();
  }, [userData]);

  // Функция для определения классов дней в календаре
  const highlightDays = (date) => {
    const adjustedDate = new Date(date);
    adjustedDate.setHours(12, 0, 0, 0);

    const dateString = adjustedDate.toLocaleDateString("en-CA");
    const dayData = calendarData[dateString];

    if (!dayData) return "";

    const classes = [];
    if (!dayData.workday_flag) classes.push("highlight-weekend");
    if (dayData.event_name) classes.push("has-event");

    if (dayData.event_name) {
      date.title = dayData.event_name;
    }

    return classes.join(" ");
  };

  const handleSubmit = async () => {
    // Предотвращаем множественные отправки
    if (isSubmitting) {
      return;
    }

    try {
      if (!selectedDate || !selectedHours) {
        setError("Пожалуйста, выберите дату и количество часов");
        return;
      }

      // Блокируем кнопку и очищаем предыдущие ошибки
      setIsSubmitting(true);
      setError(null);

      const dayOffData = {
        date_of_use: selectedDate.toISOString().split("T")[0],
        time: selectedHours.value * 3600,
        comment: "",
      };

      await createDayOff(dayOffData);

      // Очищаем форму после успешного создания
      setSelectedDate(new Date());
      setSelectedHours(null);

      // Показываем попап
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 3000);

      // Обновляем список отгулов
      await loadDayoffs();
    } catch (error) {
      console.error("Ошибка при создании заявки на отгул:", error);
      setError("Ошибка при создании заявки на отгул");
    } finally {
      // Разблокируем кнопку в любом случае
      setIsSubmitting(false);
    }
  };

  // Ограничения для выбора даты
  const today = new Date();
  const twoWeeksAgo = new Date();
  twoWeeksAgo.setDate(today.getDate() - 21);

  const fourWeeksAhead = new Date();
  fourWeeksAhead.setDate(today.getDate() + 28);

  return (
    <div className="extrahours-container">
      <div className="extrahours-top-section">
        <div className="extrahours-chart">
          <h3>Мои переработанные часы</h3>
          <div className="extrahours-chart-content">
            <Doughnut
              data={{
                labels: ["Не использовано", "Использовано"],
                datasets: [
                  {
                    data: [
                      overtimeStats["Не использовано"],
                      overtimeStats["Использовано"],
                    ],
                    backgroundColor: ["#2A5E38", "#FF9B50"],
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                cutout: "75%",
                plugins: {
                  legend: {
                    position: "bottom",
                    labels: {
                      padding: 20,
                      usePointStyle: true,
                      font: {
                        size: 14,
                        family: "Manrope",
                      },
                    },
                  },
                  tooltip: {
                    callbacks: {
                      label: function (context) {
                        const value = context.raw;
                        return `${context.label}: ${value} часов`;
                      },
                    },
                  },
                },
              }}
            />
            <div
              className="extrahours-chart-center"
              style={{
                position: "absolute",
                top: "42%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                textAlign: "center",
                width: "100%",
                maxWidth: "100px",
              }}
            >
              <p
                style={{
                  margin: "0 0 2px 0",
                  fontSize: "0.9em",
                  color: "#666",
                }}
              >
                ДОСТУПНО
              </p>
              <p
                style={{
                  margin: "0 0 2px 0",
                  fontSize: "1.1em",
                  fontWeight: "bold",
                }}
              >
                {overtimeStats["Не использовано"]} ч
              </p>
              {pendingHours > 0 && (
                <>
                  <p
                    style={{
                      fontSize: "0.75em",
                      color: "#666",
                      marginTop: "10px",
                      lineHeight: "1.1",
                      display: "block",
                      fontWeight: "bold",
                    }}
                  >
                    На рассмотрении:
                  </p>
                  <p
                    style={{
                      fontSize: "1em",
                      color: "#666",
                      margin: "0",
                      lineHeight: "1.1",
                      display: "block",
                    }}
                  >
                    {pendingHours} ч
                  </p>
                </>
              )}
            </div>
          </div>
          <p className="extrahours-chart-note">
            Примечание: Учет переработанных часов производится за предыдущие 180
            дней от текщуей даты
          </p>
        </div>

        <div className="extrahours-overtime-section">
          <OvertimeTable
            overtimeData={overtimeData}
            currentPage={overtimePage}
            totalPages={overtimeTotalPages}
            onPageChange={setOvertimePage}
          />
        </div>
      </div>

      <div className="extrahours-bottom-section">
        <div className="extrahours-form">
          <h2 className="extrahours-form__title">Оформление заявки на отгул</h2>
          {error && <div className="error-message">{error}</div>}
          <div className="form-group">
            <label>Выберите дату</label>
            <DatePicker
              selected={selectedDate}
              onChange={setSelectedDate}
              dateFormat="dd.MM.yyyy"
              locale="ru"
              className="extrahours-form__datepicker"
              minDate={twoWeeksAgo}
              maxDate={fourWeeksAhead}
              highlightDates={[
                {
                  "react-datepicker__day--highlighted": Object.keys(
                    calendarData
                  ).map((date) => new Date(date)),
                },
              ]}
              dayClassName={(date) => highlightDays(date)}
            />
          </div>

          <div className="form-group">
            <label>Выберите количество часов</label>
            <Select
              value={selectedHours}
              onChange={setSelectedHours}
              options={hoursOptions}
              placeholder={
                hoursOptions.length > 0
                  ? "Выберите часы"
                  : "Нет доступных часов для отгула"
              }
              className="hours-select"
              classNamePrefix="select"
              isDisabled={hoursOptions.length === 0}
            />
          </div>

          <button
            className="submit-button"
            onClick={handleSubmit}
            disabled={!selectedDate || !selectedHours || isSubmitting}
          >
            {isSubmitting ? "Отправка..." : "Отправить"}
          </button>
        </div>

        <DayoffTable dayoffData={dayoffData} />
      </div>

      {showPopup && (
        <div className="popup-overlay">
          <div className="popup">
            <div className="popup-icon"></div>
            <p className="popup-text">Заявка на отгул успешно отправлена</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Extrahours;
